/**
 * ClaimApprovalsTab - Settings > Claims > Approvals.
 *
 * Operates the monetary approval policy: currency bands (over-amount ->
 * approver), assignment behaviour, and the enable toggle. The page READS
 * the policy parsed from the live workflow rows and saving rewrites the
 * same rows through the platform policy writer - no parallel store.
 * Currency and strategy options come from the backend; nothing here is
 * hardcoded tenant vocabulary.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { Info, Loader2 } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import { SimpleTooltip } from '@/shared/components/ui/tooltip'
import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useApprovalLadders,
  useApprovalPolicy,
  useUpdateApprovalPolicy,
  type ApprovalPolicyResponse,
  type ApprovalPolicyWritePayload,
} from '@/admin/hooks/use-approval-policy'
import { ApprovalLaddersSection } from './approval-ladders-section'
import {
  ApprovalBandsField,
  type ApprovalBandDraft,
  type ApproverItem,
} from './approval-bands-field'

interface OrgRole {
  uuid: string
  name: string
}

interface OrgMember {
  uuid: string
  user_email: string
  user_name: string
}

/** One on/off policy setting, rendered the same way for every switch on the page. */
function PolicySwitch({
  checked,
  onToggle,
  title,
  description,
  info,
}: {
  checked: boolean
  onToggle: () => void
  title: string
  description: string
  info?: string
}) {
  return (
    <div className="col-span-2 flex items-start gap-2">
      <Button
        variant="ghost"
        type="button"
        onClick={onToggle}
        className="h-auto flex-1 justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
      >
        <Switch checked={checked} decorative />
        <div>
          <span className="block text-[12px] text-[var(--ink)]">{title}</span>
          <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
            {description}
          </span>
        </div>
      </Button>
      {info && (
        <SimpleTooltip content={info}>
          <span
            role="img"
            tabIndex={0}
            aria-label={info}
            className="mt-2.5 inline-flex cursor-help text-[var(--ink-3)] hover:text-[var(--ink)] focus-visible:text-[var(--ink)] focus-visible:outline-none"
          >
            <Info size={14} aria-hidden="true" />
          </span>
        </SimpleTooltip>
      )}
    </div>
  )
}

type PolicyFormValues = {
  bands: ApprovalBandDraft[]
  strategy: string
  notify: boolean
  lock_decision: boolean
  enabled: boolean
}

function encodeApprover(
  approver: ApprovalPolicyResponse['bands'][number]['approver'],
): string {
  if (!approver) return ''
  return `${approver.kind}:${approver.value}`
}

function decodeApprover(
  encoded: string,
): { kind: 'role' | 'person'; value: string } | null {
  if (!encoded) return null
  const separator = encoded.indexOf(':')
  if (separator < 1) return null
  const kind = encoded.slice(0, separator)
  const value = encoded.slice(separator + 1)
  if ((kind !== 'role' && kind !== 'person') || !value) return null
  return { kind, value }
}

export function ClaimApprovalsTab() {
  const { t } = useTranslation()
  const ladders = useApprovalLadders()
  // A tenant whose claim types run LOA ladders manages them here instead of a
  // single approval band.
  const hasLadders = (ladders.data?.results.length ?? 0) > 0
  const { data: policy, isLoading, isError, dataUpdatedAt } = useApprovalPolicy({
    enabled: !ladders.isPending && !hasLadders,
  })
  const [discardCounter, setDiscardCounter] = useState(0)

  if (ladders.isPending) {
    return (
      <div className="flex items-center gap-2 py-10 text-[13px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        {t('claims.approvals.loading', 'Loading approval policy...')}
      </div>
    )
  }

  if (hasLadders && ladders.data) {
    return (
      <ApprovalLaddersSection
        workflows={ladders.data.results}
        canEdit={ladders.data.can_edit}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-[13px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        {t('claims.approvals.loading', 'Loading approval policy...')}
      </div>
    )
  }

  if (isError || !policy) {
    return (
      <p className="py-10 text-[13px] text-[var(--ink-3)]">
        The approval policy could not be loaded. The claims workflow may not
        be configured for this organisation yet.
      </p>
    )
  }

  // Keyed remount: a fresh fetch (after save) or a Cancel click resets
  // the form back to the saved policy - the undo Juan asked for.
  return (
    <ApprovalPolicyEditor
      key={`${dataUpdatedAt}-${discardCounter}`}
      policy={policy}
      onDiscard={() => setDiscardCounter((n) => n + 1)}
    />
  )
}

function ApprovalPolicyEditor({
  policy,
  onDiscard,
}: {
  policy: ApprovalPolicyResponse
  onDiscard: () => void
}) {
  const { t } = useTranslation()
  const { data: org } = useOrg()
  // The org context nests the organisation - its uuid is NOT top-level.
  const orgUuid = org?.organisation?.uuid
  const update = useUpdateApprovalPolicy()

  const { data: rolesData } = useQuery<OrgRole[]>({
    queryKey: ['org-roles', orgUuid],
    queryFn: () =>
      // Every role in one request; 200 is the roles endpoint's cap (WPS-314).
      api.get(`/organisations/${orgUuid}/roles/`, { params: { page_size: 200 } }).then((r) => {
        const data = r.data?.results ?? r.data
        return Array.isArray(data) ? data : []
      }),
    enabled: !!orgUuid,
    staleTime: 60_000,
  })
  const { data: membersData } = useQuery<OrgMember[]>({
    queryKey: ['org-members', orgUuid],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/members/`).then((r) => {
        const data = r.data?.results ?? r.data
        return Array.isArray(data) ? data : []
      }),
    enabled: !!orgUuid,
    staleTime: 60_000,
  })

  const roleItems = useMemo<ApproverItem[]>(
    () =>
      (rolesData ?? []).map((role) => ({
        value: `role:${role.name}`,
        label: role.name,
      })),
    [rolesData],
  )
  const memberItems = useMemo<ApproverItem[]>(
    () =>
      (membersData ?? [])
        .filter((member) => member.user_email)
        .map((member) => ({
          value: `person:${member.user_email}`,
          label: member.user_name
            ? `${member.user_name} (${member.user_email})`
            : member.user_email,
        })),
    [membersData],
  )

  const initialValues = useMemo<PolicyFormValues>(() => {
    const bands: ApprovalBandDraft[] = policy.bands.map((band) => ({
      currency: band.currency,
      amount: band.amount,
      approver: encodeApprover(band.approver),
    }))
    if (bands.length === 0) {
      // Starter scaffold: one editable band per configured currency so a
      // tenant switching approvals on sees the basics ready to fill in
      // rather than an empty form.
      const currencies = policy.currency_options.map((option) => option.value)
      for (const currency of currencies) {
        bands.push({ currency, amount: '', approver: '' })
      }
    }
    return {
      bands,
      strategy: policy.strategy,
      notify: policy.notify,
      lock_decision: policy.lock_decision,
      // A first-time policy drafts as enabled so a single Save switches
      // the feature on; saved policies keep their stored state.
      enabled: policy.configured ? policy.enabled : true,
    }
  }, [policy])

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'custom',
        name: 'bands',
        className: 'col-span-2',
        value: initialValues.bands,
        validationSchema: z
          .array(
            z.object({
              currency: z.string(),
              amount: z.union([z.number(), z.literal('')]),
              approver: z.string(),
            }),
          )
          .superRefine((bands, ctx) => {
            const seen = new Set<string>()
            for (const band of bands) {
              if (!band.currency) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Pick a currency for every band.',
                })
                return
              }
              if (seen.has(band.currency)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'One band per currency - remove the duplicate.',
                })
                return
              }
              seen.add(band.currency)
              if (
                band.amount === '' ||
                Number.isNaN(Number(band.amount)) ||
                Number(band.amount) <= 0
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Every band needs an over-amount above zero.',
                })
                return
              }
            }
          }),
        render: (formContext) => {
          const bands = (formContext.values.bands ?? []) as ApprovalBandDraft[]
          const strategy = String(formContext.values.strategy ?? '')
          const error = formContext.errors.bands as string | undefined
          return (
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]">
                Approval bands
              </span>
              <p className="mb-2 text-[13px] text-[var(--ink-3)]">
                Require sign-off before claims over the amount are paid.
                Route to a role or a named person.
              </p>
              <ApprovalBandsField
                value={bands}
                onChange={(next) => formContext.setFieldValue('bands', next)}
                currencyOptions={policy.currency_options}
                roleItems={roleItems}
                memberItems={memberItems}
                approverDisabled={strategy === 'unassigned'}
                error={error}
              />
            </div>
          )
        },
      },
      {
        type: 'select',
        name: 'strategy',
        label: 'Assign the claim to',
        className: 'col-span-1',
        value: initialValues.strategy,
        options: policy.strategy_options,
      },
      {
        type: 'checkbox',
        name: 'notify',
        label: 'Email the assignee a link to the claim',
        className: 'col-span-1 self-end',
        value: initialValues.notify,
      },
      {
        type: 'custom',
        name: 'lock_decision',
        className: 'col-span-2',
        value: initialValues.lock_decision,
        render: (formContext) => {
          const locked = Boolean(formContext.values.lock_decision)
          return (
            <PolicySwitch
              checked={locked}
              onToggle={() => formContext.setFieldValue('lock_decision', !locked)}
              title={t('claims.approvals.lockTitle', 'Require re-approval to change an approved amount')}
              description={
                locked
                  ? t('claims.approvals.lockOn', 'Approved amounts are locked. Corrections go through Revise Decision.')
                  : t('claims.approvals.lockOff', 'Approved amounts can be edited after the decision.')
              }
              info={t(
                'claims.approvals.lockInfo',
                'Once a claim is approved, its amount and currency are locked. To change them, use Revise Decision - the claim returns to review and needs sign-off again if it is over the limit.',
              )}
            />
          )
        },
      },
      {
        type: 'custom',
        name: 'enabled',
        className: 'col-span-2',
        value: initialValues.enabled,
        render: (formContext) => {
          const enabled = Boolean(formContext.values.enabled)
          return (
            <PolicySwitch
              checked={enabled}
              onToggle={() => formContext.setFieldValue('enabled', !enabled)}
              title={
                enabled
                  ? t('claims.approvals.enabledTitle', 'Approvals enabled')
                  : t('claims.approvals.pausedTitle', 'Approvals paused')
              }
              description={t(
                'claims.approvals.enabledHint',
                'When paused, claims pay without sign-off. Your bands are kept and restored when you enable again.',
              )}
            />
          )
        },
      },
    ],
    [initialValues, policy, roleItems, memberItems, t],
  )

  const { Form, FormFooter } = useForm(
    {
      name: 'approval-policy-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as PolicyFormValues
      const payload: ApprovalPolicyWritePayload = {
        enabled: values.enabled,
        bands: values.bands.map((band) => ({
          currency: band.currency,
          amount: Number(band.amount),
          approver:
            values.strategy === 'unassigned'
              ? null
              : decodeApprover(band.approver),
        })),
        strategy: values.strategy,
        notify: values.notify,
        lock_decision: values.lock_decision,
      }
      try {
        const result = await update.mutateAsync(payload)
        toast.success('Approval policy saved')
        for (const warning of result.warnings ?? []) {
          toast.warning(warning)
        }
      } catch (err) {
        toastApiError(err, 'Failed to save the approval policy')
      }
    },
    [policy, fields],
  )

  return (
    <div className="flex max-w-[860px] flex-col gap-4">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-[28px] font-medium leading-[1.1] text-[var(--ink)]">
            Claim approvals
          </h2>
          <p className="mt-1 text-[13px] text-[var(--ink-2)]">
            Require sign-off before high-value claims are paid.
          </p>
        </div>
        <Badge variant={policy.enabled ? 'default' : 'outline'}>
          {policy.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {policy.pending_count > 0 && (
        <p className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2.5 text-[12px] text-[var(--ink-2)]">
          {policy.pending_count} claim(s) currently sit in the approval step.
        </p>
      )}

      {!policy.configured && (
        <p className="rounded-lg border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-4 py-2.5 text-[12px] text-[var(--ink-2)]">
          <span className="font-medium text-[var(--green)]">
            Starter policy.{' '}
          </span>
          Nothing is live yet - a band per currency is drafted below. Set the
          over-amounts, pick an approver if you want a named person, and Save
          changes to switch approvals on.
        </p>
      )}

      <div className="rounded-xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-6 py-5">
        <Form className="grid grid-cols-2 gap-x-5 gap-y-5" />
        <p className="mt-3 text-[11px] leading-[1.5] text-[var(--ink-3)]">
          When a claim is auto-assigned for approval, the approver is always
          someone other than the person who filed it or currently handles it.
          If no one else is eligible, the claim waits in the approval queue for
          a manual pick.
        </p>
        <div className="mt-5 flex justify-end gap-2.5 border-t-[0.5px] border-[var(--rule)] pt-4">
          {FormFooter && (
            <FormFooter
              onCancel={onDiscard}
              submitText={update.isPending ? 'Saving...' : 'Save changes'}
            />
          )}
        </div>
      </div>
    </div>
  )
}
